import { col, type Shape } from './base.ts';

export const ALARM_ENTITY_RELATION: Shape = [
  col('entityName', 'unitless', 'Display name of the entity (well, string) with the alarm.'),
  col('entityExternalId', 'unitless', 'External id of the alarmed entity, for joining to other views.'),
  col('entityType', 'unitless', 'Type of alarmed entity (string, well, field).'),
];

/**
 * The shared alarm-event measure base common to ALL ten alarm sources (the nine
 * per-category sources + the `WfAlarms_Agent` superset). Every column is
 * a bare canonical source name (`suffix === canonical`, `prefix:''`, pure
 * passthrough). Deliberately EXCLUDES the columns that vary per source —
 * `startDateTime`/`endDateTime` (`ALARM_TIMESTAMPS`),
 * `alarmCounter`/`ticketId` (`ALARM_COUNTER_TICKET`), and the Wf-only
 * `alarmCategory`/`alarmMessage` (`ALARM_SUPERSET_EXTRAS`) — which are composed
 * per source where the source actually exposes them, so every authored suffix
 * resolves (no `unknownCol`). Also EXCLUDES the source's `alarmCode` column: it
 * is an opaque `z.json()` reference to the AlarmDefinition node with no
 * resolvable engine type (`viewToSchema` yields `unknown`), so projecting it
 * would degrade to `unknownCol` and break the no-unknownCol guard;
 * `alarmCodeExternalId` is the usable, string-typed alarm-code identifier and is
 * kept in its place.
 */
export const ALARM_EVENT: Shape = [
  col('alarmCodeExternalId', 'unitless', 'Alarm code identifier (e.g. WF12-001).'),
  col('alarmName', 'unitless', 'Human-readable alarm name from AlarmDefinition.'),
  col('alarmPriority', 'unitless', 'Alarm priority level (e.g. HIGH/MEDIUM/LOW).'),
  col('alarmCriticality', 'unitless', 'Alarm criticality level.'),
  col('alarmFlags', 'unitless', 'Alarm validity flag (Valid, Invalid, …).'),
  col(
    'customWorkflowAlarmMessage',
    'unitless',
    'Detailed alarm message with the specific conditions that triggered it.',
  ),
  col('subCategory', 'unitless', 'Alarm sub-category for domain filtering.'),
  col('workflowCode', 'unitless', 'Workflow code from AlarmDefinition (e.g. WF12).'),
  col('workflowName', 'unitless', 'Workflow name from AlarmDefinition.'),
  col('subWorkflowName', 'unitless', 'Sub-workflow that generated the alarm.'),
];

/**
 * The alarm trigger/clear timestamps: `startDateTime` (required) +
 * `endDateTime` (nullish). Composed onto every alarm source EXCEPT
 * `ESPAlarms_Agent` and `WfAlarms_Agent`, whose generated views drop them via
 * `.omit({startDateTime,endDateTime})` — authoring them there would
 * `unknownCol`. Bare canonical source columns (`suffix === canonical`,
 * `prefix:''`).
 */
export const ALARM_TIMESTAMPS: Shape = [
  col('startDateTime', 'unitless', 'When the alarm was triggered.'),
  col('endDateTime', 'unitless', 'When the alarm was cleared.'),
];

/**
 * The occurrence counter + troubleshooting ticket id. Composed only onto
 * the sources that expose them — `ESPAlarms_Agent`, `GasLiftAlarms_Agent`,
 * `InjectionAlarms_Agent`, `NetworkAlarms_Agent`,
 * `ProductionAllocationAlarms_Agent`, and the `WfAlarms_Agent` superset — the
 * other four per-category sources lack both, so authoring them there would
 * `unknownCol`. Bare canonical source columns (`suffix === canonical`,
 * `prefix:''`).
 */
export const ALARM_COUNTER_TICKET: Shape = [
  col('alarmCounter', 'unitless', 'Count of how many times this alarm has occurred.'),
  col('ticketId', 'unitless', 'Associated troubleshooting ticket id, if one was created.'),
];

/**
 * The `WfAlarms_Agent` superset-only extras kept by the `AllAlarms` view
 * The `alarmCategory` (DROPPED by the nine per-category views, whose
 * category is implied by the view name) and the Wf-only `alarmMessage` (the
 * standard alarm-message template, absent from all nine per-category sources).
 * Bare canonical source columns (`suffix === canonical`, `prefix:''`).
 */
export const ALARM_SUPERSET_EXTRAS: Shape = [
  col('alarmCategory', 'unitless', 'Alarm category from AlarmDefinition (e.g. ESP, Gas Lift, Production).'),
  col('alarmMessage', 'unitless', 'Standard alarm-message template from AlarmDefinition.'),
];
/**
 * The Opportunity record measure family on `Opportunity_Agent`: the 25
 * non-relation scalar columns describing one production-improvement opportunity
 * — its classification (`type`/`status`/`source`/`owner`), free-text
 * (`summary`/`description`/`interventionActivity`), lifecycle dates, the
 * before/after rate estimates, cumulative realized gain, and the
 * validation/value-tracking outcome. The source already exposes the
 * nested baseline/estimate/validation/valueTracking groups as FLAT scalar
 * columns (`baselineOilRate`, `validationStatus`, `valueTrackingStatus`, …) — so
 * there is nothing to collapse; every entry is authored as its verbatim
 * canonical source name. Bare canonical source columns (`suffix === canonical`,
 * `prefix:''`), so the reshape emits only a `(columns …)` projection and no
 * `(rename …)`. The well-grain ref (`wellString`) is kept via `STRING_RELATION`,
 * not here. EXCLUDES `__typename` (the Zod literal discriminator, not a data
 * column). The dates are `z.iso.date()` strings.
 */
